import { LuIcon, Menu, type MenuRootProps, type MenuVariantProps, toast } from "@overmap-ai/blocks"
import { type GeoImage, type Stored, ERROR_MSG_UNEXPECTED } from "@overmap-ai/common"
import { APIError } from "@overmap-ai/core"
import { useImageViewer } from "@overmap-ai/forms"
import { useOpenState } from "@overmap-ai/hooks"
import { memo, useCallback } from "react"

import { useConfirmDeleteDialog, useFetchFileFromFileModel } from "../../hooks/utils"
import { modelIconMapping } from "../../mappings/icons"
import { sdk } from "../../sdk"
import { GeoImageDetailsDialog } from "../Dialogs/GeoImageDetailsDialog"
import { LocationGroup } from "../MenuGroups/LocationGroup"

interface GeoImageMarkerContextMenu extends MenuRootProps, MenuVariantProps {
	geoImage: Stored<GeoImage>
	onDelete?: (geoImage: Stored<GeoImage>) => void
}

export const GeoImageMarkerContextMenu = memo<GeoImageMarkerContextMenu>((props) => {
	const { geoImage, accentColor, variant, size, radius, children, onDelete, ...rest } = props

	const openImageViewer = useImageViewer()
	const openConfirmDeleteDialog = useConfirmDeleteDialog()
	const [showDetailsDialog, openDetailsDialog, closeDetailsDialog] = useOpenState(false)

	const [file] = useFetchFileFromFileModel(geoImage, sdk.geoImages)

	const handleView = useCallback(() => {
		if (!file) return

		openImageViewer((close) => ({
			file: file,
			onClose: close,
		}))
	}, [file, openImageViewer])

	const handleDelete = useCallback(() => {
		openConfirmDeleteDialog({
			title: "Delete Geo Image",
			description: "Are you sure you want to delete this geo image?",
			onAction: () => {
				onDelete?.(geoImage)
				sdk.geoImages.delete(geoImage.uuid).catch((error) => {
					toast.error({
						title: "Failed to delete geo image",
						description: error instanceof APIError ? error.message : ERROR_MSG_UNEXPECTED,
					})
				})
			},
		})
	}, [geoImage, onDelete, openConfirmDeleteDialog])

	return (
		<>
			<Menu.Root {...rest}>
				{children}
				<Menu.Content accentColor={accentColor} variant={variant} size={size} radius={radius}>
					<Menu.Group>
						<Menu.Item onSelect={handleView}>
							<LuIcon icon={modelIconMapping.expand} />
							View
						</Menu.Item>
						<Menu.Item onSelect={openDetailsDialog}>
							<LuIcon icon={modelIconMapping.edit} />
							Edit
						</Menu.Item>
						<Menu.Separator />
						<LocationGroup coordinates={geoImage.geo_marker?.coordinates} />
						<Menu.Separator />
						<Menu.Item onSelect={handleDelete}>
							<LuIcon icon={modelIconMapping.delete} />
							Delete
						</Menu.Item>
					</Menu.Group>
				</Menu.Content>
			</Menu.Root>
			<GeoImageDetailsDialog
				geoImageId={geoImage.uuid}
				open={showDetailsDialog}
				onOpenChange={closeDetailsDialog}
				size="sm"
			/>
		</>
	)
})
GeoImageMarkerContextMenu.displayName = "GeoImageMarkerContextMenu"
